import React, { useContext, useEffect } from 'react';

import { Box, makeStyles, Theme, createStyles } from '@material-ui/core';

import { GetStarted } from './components/GetStarted';
import { AI_CHAT_FILE_ACCEPTS, AI_TOOL_ACTIVATED, DOCUMENT_ANALYSIS_IDEAS } from './constants';
import { AIChatContext } from './context';
import { IdeasDialog } from './components/IdeasDialog';
import { TipsDialog } from './components/TipsDialog';
import { LailaWelcome } from './components/LailaWelcome';
import { LailaThread } from './components/thread/LailaThread';
import { LailaComposer } from './components/composer/LailaComposer';
import { ChatDropZone } from '../../components/Chat/kit';
import { useBooleanState } from '../../hooks';
import { getFromLS } from '../../utils/local-storage';

export const AIChat: React.FC = () => {
  const classes = useStyles();
  const {
    activateAIChat,
    isAIActivated,
    messages,
    setIsAIActivated,
    thinking,
    status: answerStatus,
    retry,
    uploadFile,
  } = useContext(AIChatContext);
  const [openTipsDialog, setOpenTipsDialog, setCloseTipsDialog] = useBooleanState(false);
  const [openIdeasDialog, setOpenIdeasDialog, setCloseIdeasDialog] = useBooleanState(false);

  // Files dropped anywhere on the chat are attached to the next question.
  const onDrop = async (accepted: File[]) => {
    for (const file of accepted) {
      await uploadFile(file);
    }
  };

  useEffect(() => {
    const status = getFromLS(AI_TOOL_ACTIVATED);
    setIsAIActivated(!!status);
  }, []);

  // The chat fills the screen and scrolls inside itself.
  useEffect(() => {
    if (isAIActivated) {
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    };
  }, [isAIActivated]);

  if (!isAIActivated) {
    return <GetStarted onStartClick={activateAIChat} />;
  }

  return (
    <ChatDropZone
      className={classes.page}
      accept={AI_CHAT_FILE_ACCEPTS}
      onDrop={onDrop}
      label="Drop files to ask Laila about them"
    >
      <LailaThread
        className={classes.thread}
        messages={messages}
        thinking={thinking}
        status={answerStatus}
        onRetry={retry}
        emptyState={<LailaWelcome onShowIdeas={setOpenIdeasDialog} onShowTips={setOpenTipsDialog} />}
      />

      <Box className={classes.composer}>
        <LailaComposer fileSuggestions={DOCUMENT_ANALYSIS_IDEAS} />
      </Box>

      <IdeasDialog open={openIdeasDialog} handleClose={setCloseIdeasDialog} onIdeaClick={() => {}} />

      <TipsDialog open={openTipsDialog} handleClose={setCloseTipsDialog} />
    </ChatDropZone>
  );
};

// The app bar is 64px tall, 56px on phones; below `sm` the layout pads the sides by 16px.
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    page: {
      display: 'flex',
      flexDirection: 'column',
      fallbacks: { height: 'calc(100vh - 64px)' },
      height: 'calc(100dvh - 64px)',
      backgroundColor: theme.palette.background.default,
      [theme.breakpoints.down('sm')]: { margin: '0 -16px' },
      [theme.breakpoints.down('xs')]: {
        fallbacks: { height: 'calc(100vh - 56px)' },
        height: 'calc(100dvh - 56px)',
      },
    } as any,
    thread: { flex: 1 },
    composer: {
      width: '100%',
      maxWidth: 792,
      margin: '0 auto',
      padding: '4px 16px 16px',
      [theme.breakpoints.down('xs')]: { padding: '4px 8px 8px' },
    },
  })
);
